'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';

const concepts = [
  {
    label: 'CREATION AS CLASSROOM',
    ar: 'الْخَلْق كَمَدْرَسَة',
    desc: 'Every element of the environment is a teacher. The horizon is a lesson. The tree is a text. The sky is a sign.',
    depth: 'The curriculum does not end at the door of the masjid. It continues in the watershed, the sidewalk crack, the vacant lot, the canopy. Every created thing is a verse (Ayah) in the ongoing revelation of the Real. The Keys you have learned are not concepts to carry — they are lenses to wear. Step outside. The classroom has always been open.',
    ayat: '41:53',
    color: '#2d7a47',
  },
  {
    label: 'AYAT IN THE HORIZONS',
    ar: 'آيَاتٌ فِي الْآفَاق',
    desc: 'Quran 41:53 — Soon We will show them Our Signs in the horizons and in themselves until it becomes clear that it is the Truth.',
    depth: 'The Afaq (horizons) and the Anfus (selves) are the two registers of the Lens. The external world and the internal world are both classrooms. The IRL Lens operates in both simultaneously — the leaf outside and the breath inside are both Ayat. The horizon is not a metaphor. It is the literal edge of what you can see — and beyond it, more signs.',
    ayat: '41:53',
    color: '#c9a84c',
  },
  {
    label: 'DOMAIN EXPANSION',
    ar: 'تَوَسُّع الْمَجَال',
    desc: 'Awareness of the ever-present veil. The curriculum keys expand your domain of perception — what was invisible becomes legible.',
    depth: 'Before the Keys, you saw a tree. After the Keys, you see Al-Khaliq, Al-Bari, Al-Musawwir, Ar-Razzaq, An-Nur, Al-Qadir — all composing what you called "a tree." The domain of perception has expanded. The veil has not been removed — it has become transparent. You are not seeing more things. You are seeing the same things more completely.',
    ayat: '7:26',
    color: '#2d8a87',
  },
  {
    label: 'THE VEIL BECOMES VISIBLE',
    ar: 'الْحِجَاب يَصِير مَرْئِيًّا',
    desc: 'The bridge is not a destination but a passage. From the internal keys to the external world — the same reality, newly perceived.',
    depth: 'The Zahir (apparent) and the Batin (hidden) are not two different realities — they are two depths of the same reality. The veil between them is not opaque; it is translucent. The Keys have trained your eyes to see through it. This bridge is the moment of crossing — from knowing the Keys to using them. From understanding the Lens to looking through it.',
    ayat: '57:3',
    color: '#8a6a9a',
  },
];

const PAUSE_PROMPTS = [
  'Step outside. Look at one thing. Now continue.',
  'What is the nearest created thing to you right now? Name it. Then ask: which Names compose it?',
  'Before you cross: what is your Haal in this moment? What inner weather are you bringing to the external world?',
  'The bridge is a threshold. What are you leaving behind? What are you carrying forward?',
];

export default function BridgeContent() {
  const [expandedCard, setExpandedCard] = useState<number | null>(null);
  const [pauseStep, setPauseStep] = useState(0);
  const [pauseComplete, setPauseComplete] = useState(false);
  const [pauseVisible, setPauseVisible] = useState(false);
  const [crossingReady, setCrossingReady] = useState(false);
  const [breathCount, setBreathCount] = useState(0);
  const [breathPhase, setBreathPhase] = useState<'in' | 'hold' | 'out' | 'idle'>('idle');
  const breathRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setPauseVisible(true); },
      { threshold: 0.3 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  function startBreath() {
    if (breathRef.current) return;
    setBreathPhase('in');
    let phase: 'in' | 'hold' | 'out' = 'in';
    let count = 0;
    const cycle = () => {
      if (phase === 'in') { phase = 'hold'; setBreathPhase('hold'); setTimeout(cycle, 4000); }
      else if (phase === 'hold') { phase = 'out'; setBreathPhase('out'); setTimeout(cycle, 4000); }
      else {
        count++;
        setBreathCount(count);
        if (count >= 1) {
          setBreathPhase('idle');
          setPauseComplete(true);
          setCrossingReady(true);
          return;
        }
        phase = 'in'; setBreathPhase('in'); setTimeout(cycle, 4000);
      }
    };
    setTimeout(cycle, 4000);
  }

  const breathLabel = breathPhase === 'in' ? 'Breathe In' : breathPhase === 'hold' ? 'Hold' : breathPhase === 'out' ? 'Breathe Out' : pauseComplete ? 'Complete' : 'Begin';
  const breathColor = breathPhase === 'in' ? '#2d7a47' : breathPhase === 'hold' ? '#c9a84c' : breathPhase === 'out' ? '#2d8a87' : pauseComplete ? '#c9a84c' : 'rgba(245,240,232,0.3)';

  return (
    <section ref={sectionRef} className="py-24 bg-black border-t border-white/10" id="bridge-content">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-8">

        {/* Quran verse */}
        <div className="max-w-2xl mx-auto text-center mb-20">
          <p className="font-mono text-xs tracking-widest text-white/20 uppercase mb-6">Quran 41:53</p>
          <blockquote className="font-mono text-base md:text-lg text-white/60 leading-relaxed">
            &ldquo;Soon We will show them Our Signs in the horizons and in themselves until it becomes clear that it is the Truth. Is it not sufficient concerning your Lord that He is over all things a Witness?&rdquo;
          </blockquote>
        </div>

        {/* ── EXPERIENTIAL PAUSE MOMENT ── */}
        <div
          className="max-w-2xl mx-auto mb-20 transition-all duration-1000"
          style={{ opacity: pauseVisible ? 1 : 0, transform: pauseVisible ? 'translateY(0)' : 'translateY(24px)' }}
        >
          <div
            className="relative overflow-hidden"
            style={{
              border: '1px solid rgba(139,105,20,0.25)',
              background: 'linear-gradient(135deg, rgba(26,74,46,0.06), rgba(139,105,20,0.04))',
              padding: '40px 32px',
            }}
          >
            {/* Corner marks */}
            <div style={{ position: 'absolute', top: 8, left: 8, width: 12, height: 12, borderTop: '1px solid rgba(139,105,20,0.4)', borderLeft: '1px solid rgba(139,105,20,0.4)' }} />
            <div style={{ position: 'absolute', top: 8, right: 8, width: 12, height: 12, borderTop: '1px solid rgba(139,105,20,0.4)', borderRight: '1px solid rgba(139,105,20,0.4)' }} />
            <div style={{ position: 'absolute', bottom: 8, left: 8, width: 12, height: 12, borderBottom: '1px solid rgba(139,105,20,0.4)', borderLeft: '1px solid rgba(139,105,20,0.4)' }} />
            <div style={{ position: 'absolute', bottom: 8, right: 8, width: 12, height: 12, borderBottom: '1px solid rgba(139,105,20,0.4)', borderRight: '1px solid rgba(139,105,20,0.4)' }} />

            <p className="font-mono text-xs tracking-widest uppercase text-center mb-2" style={{ color: 'rgba(139,105,20,0.5)' }}>Liminal Threshold</p>
            <p className="font-mono text-xs tracking-widest uppercase text-center mb-8" style={{ color: 'rgba(245,240,232,0.2)' }}>Before you cross — a moment of orientation</p>

            {/* Breath circle */}
            <div className="flex flex-col items-center mb-8">
              <div
                className="relative flex items-center justify-center cursor-pointer mb-4"
                style={{ width: 100, height: 100 }}
                onClick={() => { if (breathPhase === 'idle' && !pauseComplete) startBreath(); }}
              >
                {/* Outer ring */}
                <div
                  style={{
                    position: 'absolute', inset: 0, borderRadius: '50%',
                    border: `1px solid ${breathColor}`,
                    transition: 'all 4s ease',
                    transform: breathPhase === 'in' ? 'scale(1.15)' : breathPhase === 'out' ? 'scale(0.9)' : 'scale(1)',
                    opacity: breathPhase === 'idle' ? 0.3 : 0.7,
                  }}
                />
                {/* Inner fill */}
                <div
                  style={{
                    position: 'absolute', inset: 12, borderRadius: '50%',
                    background: `radial-gradient(circle, ${breathColor}20, transparent)`,
                    transition: 'all 4s ease',
                    transform: breathPhase === 'in' ? 'scale(1.2)' : breathPhase === 'out' ? 'scale(0.8)' : 'scale(1)',
                  }}
                />
                <p className="font-mono text-xs text-center z-10" style={{ color: breathColor, transition: 'color 1s' }}>
                  {breathLabel}
                </p>
              </div>
              {breathPhase === 'idle' && !pauseComplete && (
                <p className="font-mono text-xs text-center" style={{ color: 'rgba(245,240,232,0.25)' }}>
                  Click to begin one breath cycle
                </p>
              )}
              {pauseComplete && (
                <p className="font-mono text-xs text-center" style={{ color: 'rgba(139,105,20,0.6)' }}>
                  ✦ The breath is complete. The crossing is ready.
                </p>
              )}
            </div>

            {/* Rotating prompts */}
            <div className="text-center mb-6">
              <p className="font-mono text-sm leading-relaxed" style={{ color: 'rgba(245,240,232,0.5)', fontStyle: 'italic' }}>
                &ldquo;{PAUSE_PROMPTS[pauseStep]}&rdquo;
              </p>
              <div className="flex items-center justify-center gap-2 mt-4">
                {PAUSE_PROMPTS.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setPauseStep(i)}
                    style={{
                      width: 6, height: 6,
                      borderRadius: '50%',
                      background: i === pauseStep ? 'rgba(139,105,20,0.8)' : 'rgba(255,255,255,0.1)',
                      border: 'none', cursor: 'pointer', padding: 0, transition: 'all 0.2s',
                    }}
                  />
                ))}
              </div>
            </div>

            {/* Skip option */}
            {!pauseComplete && (
              <div className="text-center">
                <button
                  onClick={() => { setPauseComplete(true); setCrossingReady(true); }}
                  className="font-mono text-xs"
                  style={{ color: 'rgba(245,240,232,0.2)', background: 'none', border: 'none', cursor: 'pointer', letterSpacing: '.08em' }}
                >
                  skip pause →
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ── CONCEPT CARDS — EXPANDED ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 mb-20">
          {concepts.map((c, i) => (
            <div
              key={c.label}
              className="bg-black transition-all duration-300 cursor-pointer"
              style={{ background: expandedCard === i ? 'rgba(255,255,255,0.03)' : 'black' }}
              onClick={() => setExpandedCard(expandedCard === i ? null : i)}
            >
              <div className="p-8">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="font-mono text-xs font-black tracking-widest uppercase mb-1" style={{ color: c.color }}>{c.label}</p>
                    <p className="font-mono text-xs" style={{ color: 'rgba(245,240,232,0.2)', direction: 'rtl' }}>{c.ar}</p>
                  </div>
                  <div
                    className="font-mono text-xs flex-shrink-0 ml-4"
                    style={{ color: expandedCard === i ? c.color : 'rgba(255,255,255,0.2)', transition: 'color 0.2s' }}
                  >
                    {expandedCard === i ? '−' : '+'}
                  </div>
                </div>
                <p className="font-mono text-sm leading-relaxed mb-0" style={{ color: 'rgba(245,240,232,0.4)' }}>{c.desc}</p>

                {expandedCard === i && (
                  <div className="mt-6 pt-6" style={{ borderTop: `1px solid ${c.color}20` }}>
                    <p className="font-mono text-sm leading-relaxed mb-4" style={{ color: 'rgba(245,240,232,0.6)' }}>{c.depth}</p>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs" style={{ color: `${c.color}60` }}>Quran {c.ayat}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* ── BRIDGE VISUAL ── */}
        <div className="mt-4 flex items-center justify-center gap-4 mb-16">
          <div className="text-center">
            <div className="border border-white/20 px-6 py-4 mb-2">
              <p className="font-mono text-xs font-black text-white tracking-widest">KEYS TO KULLI HAAL</p>
            </div>
            <p className="font-mono text-xs text-white/20">Internal Framework</p>
          </div>
          <div className="flex-1 max-w-32 h-px relative" style={{ background: 'linear-gradient(90deg, rgba(255,255,255,0.2), rgba(179,138,42,0.6), rgba(255,255,255,0.2))' }}>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-white rotate-45" />
          </div>
          <div className="text-center">
            <div className="border border-white/20 px-6 py-4 mb-2">
              <p className="font-mono text-xs font-black text-white tracking-widest">ENVIRONMENT DESIGN</p>
            </div>
            <p className="font-mono text-xs text-white/20">External World</p>
          </div>
        </div>

        {/* ── CROSSING CTA ── */}
        <div className="flex flex-col items-center gap-4">
          {crossingReady ? (
            <div className="text-center">
              <p className="font-mono text-xs tracking-widest uppercase mb-4" style={{ color: 'rgba(139,105,20,0.5)' }}>
                ✦ The breath is complete. The crossing is ready.
              </p>
              <Link
                href="/ayat-in-creation"
                className="font-mono text-xs tracking-widest uppercase text-black bg-white px-8 py-4 hover:bg-white/80 transition-colors inline-block"
                style={{ letterSpacing: '.12em' }}
              >
                Continue: Ayat in Creation →
              </Link>
            </div>
          ) : (
            <div className="text-center">
              <p className="font-mono text-xs mb-4" style={{ color: 'rgba(245,240,232,0.2)' }}>
                Complete the pause above to cross, or continue directly
              </p>
              <Link
                href="/ayat-in-creation"
                className="font-mono text-xs tracking-widest uppercase px-6 py-3 transition-colors inline-block"
                style={{ border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(245,240,232,0.4)' }}
              >
                Continue: Ayat in Creation →
              </Link>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
